import React, { useMemo } from "react";

import { ColumnDef, createColumnHelper } from "@tanstack/react-table";

import { useHistoryDecisionsV3 } from "src/api/decisionHistoryV2/decisionHistoryQueries";
import { Flow } from "src/api/flowTypes";
import { useFlows } from "src/api/queries";
import { DecisionEnvironment } from "src/api/types";
import { TruncatedIdPill } from "src/base-components/TruncatedIdPill";
import { WorkspaceDataplane } from "src/clients/flow-api";
import { HistoricalDecisionApiResponse } from "src/clients/history_v3";
import { RawCell } from "src/dataTable/RawCell";
import { TableComp } from "src/flow/decisionHistory/Table";
import { formatDate } from "src/utils/datetime";

type Props = {
  workspace: WorkspaceDataplane;
};

const columnHelper = createColumnHelper<HistoricalDecisionApiResponse>();

const Header: React.FC<{ children: string; className?: string }> = ({
  children,
  className,
}) => (
  <div
    className={`whitespace-nowrap p-1.5 font-inter-medium-12px text-gray-800 ${className ?? ""}`}
  >
    {children}
  </div>
);

const buildColumns = (
  flowsById: Map<string, Flow>,
): ColumnDef<HistoricalDecisionApiResponse, unknown>[] =>
  [
    columnHelper.accessor("id", {
      id: "decision_id",
      size: 50,
      header: () => <Header>Decision ID</Header>,
      cell: ({ cell }) => (
        <RawCell cell={<TruncatedIdPill id={cell.getValue() ?? ""} />} />
      ),
    }),
    columnHelper.accessor("start_time", {
      id: "start_time",
      size: 70,
      header: () => <Header className="w-24">Time</Header>,
      cell: ({ cell }) => {
        const value = cell.getValue();
        return (
          <RawCell
            cell={
              <>{value ? formatDate(value, "dd MMM yy HH:mm:ss") : "N/A"}</>
            }
            classNameOverrides="max-w-none w-36 p-1.5"
          />
        );
      },
    }),
    columnHelper.accessor(
      (row) => flowsById.get(row.flow?.id)?.name ?? "Unknown",
      {
        id: "flow_name",
        size: 120,
        header: () => <Header>Flow</Header>,
        cell: ({ cell }) => (
          <RawCell
            cell={<>{cell.getValue()}</>}
            classNameOverrides="max-w-none p-1.5"
          />
        ),
      },
    ),
    columnHelper.accessor("environment", {
      id: "environment",
      size: 80,
      header: () => <Header>Environment</Header>,
      cell: ({ cell }) => (
        <RawCell cell={<>{cell.getValue()}</>} classNameOverrides="p-1.5" />
      ),
    }),
  ] as ColumnDef<HistoricalDecisionApiResponse, unknown>[];

export const PendingDecisionsDebugSection: React.FC<Props> = ({
  workspace,
}) => {
  const flowsQuery = useFlows({ workspaceId: workspace.id });
  const allFlowIds = useMemo(
    () => (flowsQuery.data ?? []).map((flow: Flow) => flow.id).join(","),
    [flowsQuery.data],
  );

  const pendingDecisions = useHistoryDecisionsV3({
    baseUrl: workspace.base_url!,
    environments: DecisionEnvironment.LIVE,
    flowIds: allFlowIds,
    startStartTime: "2020-01-01T00:00:00Z",
    endStartTime: "2099-01-01T00:00:00Z",
    statusCodes: "202",
    enabled: allFlowIds.length > 0,
  });

  const flowsById = useMemo(
    () => new Map((flowsQuery.data ?? []).map((flow: Flow) => [flow.id, flow])),
    [flowsQuery.data],
  );

  const columns = useMemo(() => buildColumns(flowsById), [flowsById]);

  const decisions = useMemo(
    () => pendingDecisions.data?.pages.flatMap((page) => page.decisions) ?? [],
    [pendingDecisions.data],
  );

  const isLoading = flowsQuery.isLoading || pendingDecisions.isLoading;

  if (flowsQuery.isError || pendingDecisions.isError) {
    return (
      <div className="font-inter-normal-12px text-red-500">
        Error loading pending decisions
      </div>
    );
  }

  if (!isLoading && decisions.length === 0) {
    return (
      <div className="font-inter-normal-12px text-gray-500">
        No pending decisions found in this workspace
      </div>
    );
  }

  return (
    <>
      <h2 className="mt-4 mb-3 font-inter-semibold-14px text-gray-800">
        Pending Decisions ({decisions.length})
      </h2>
      <TableComp
        canFetchNextPage={Boolean(
          !pendingDecisions.isFetching && pendingDecisions.hasNextPage,
        )}
        columns={columns}
        data={decisions}
        dataLoc="pending-decisions-table"
        fetchNextPage={pendingDecisions.fetchNextPage}
        frameClassName="max-h-[640px] overflow-auto"
        isFetching={isLoading || pendingDecisions.isFetchingNextPage}
        onDecisionClick={{ predicate: () => false, effect: () => {} }}
      />
    </>
  );
};
